import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, DollarSign, FileText, TrendingUp, Award, Plus } from "lucide-react";
import { AthleteSidebar } from "@/components/athlete/AthleteSidebar";

const AthletePortal = () => {
  const navigate = useNavigate();
  const [athleteId, setAthleteId] = useState<string | null>(null);

  useEffect(() => {
    const checkAthleteProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data: athleteProfile } = await supabase
        .from("athlete_profiles")
        .select("athlete_id")
        .eq("user_id", user.id)
        .single();

      if (athleteProfile) {
        setAthleteId(athleteProfile.athlete_id);
      } else {
        // Not an athlete, redirect to main dashboard
        navigate("/dashboard");
      }
    };

    checkAthleteProfile();
  }, [navigate]);

  const { data: athleteData, isLoading: athleteLoading } = useQuery({
    queryKey: ["athlete-profile", athleteId],
    queryFn: async () => {
      if (!athleteId) return null;
      const { data, error } = await supabase
        .from("athletes")
        .select("*")
        .eq("id", athleteId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!athleteId,
  });

  const { data: deals, isLoading: dealsLoading } = useQuery({
    queryKey: ["athlete-deals", athleteId],
    queryFn: async () => {
      if (!athleteId) return [];
      const { data, error } = await supabase
        .from("deals")
        .select("*")
        .eq("athlete_id", athleteId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!athleteId,
  });

  const { data: compensation, isLoading: compensationLoading } = useQuery({
    queryKey: ["athlete-compensation", athleteId],
    queryFn: async () => {
      if (!athleteId) return [];
      const { data, error } = await supabase
        .from("athlete_compensation")
        .select("*")
        .eq("athlete_id", athleteId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!athleteId,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      draft: "secondary",
      pending_compliance: "default",
      active: "default",
      rejected: "destructive",
      cancelled: "secondary",
    };
    return colors[status as keyof typeof colors] || "secondary";
  };

  const activeDeals = deals?.filter(d => d.status === "active") || [];
  const pendingDeals = deals?.filter(d => d.status === "pending_compliance") || [];
  const totalEarnings = deals?.filter(d => d.status === "active").reduce((sum, d) => sum + Number(d.amount), 0) || 0;
  const pendingCompensation = compensation?.filter(c => c.status === "pending").reduce((sum, c) => sum + Number(c.amount), 0) || 0;

  if (athleteLoading || dealsLoading || compensationLoading) {
    return (
      <div className="flex h-screen">
        <AthleteSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <AthleteSidebar />
      <div className="flex-1 overflow-y-auto p-8">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Athlete Portal</h1>
            <p className="text-muted-foreground mt-2">
              Welcome back, {athleteData?.name}
            </p>
          </div>
          <Button onClick={() => navigate("/athlete/deals/submit")}>
            <Plus className="mr-2 h-4 w-4" />
            Submit New Deal
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Deals</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeDeals.length}</div>
              <p className="text-xs text-muted-foreground">
                {pendingDeals.length} pending approval
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(totalEarnings)}</div>
              <p className="text-xs text-muted-foreground">
                From active deals
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Compensation</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(pendingCompensation)}</div>
              <p className="text-xs text-muted-foreground">
                Revenue sharing pools
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Eligibility Status</CardTitle>
              <Award className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold capitalize">
                {athleteData?.eligibility_status || "N/A"}
              </div>
              <p className="text-xs text-muted-foreground">
                {athleteData?.sport} • {athleteData?.year}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Deals */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Deals</CardTitle>
              <Button variant="outline" size="sm" onClick={() => navigate("/athlete/deals")}>
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {deals && deals.length > 0 ? (
              <div className="space-y-4">
                {deals.slice(0, 5).map((deal) => (
                  <div
                    key={deal.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/athlete/deals/${deal.id}`)}
                  >
                    <div className="flex-1">
                      <h4 className="font-medium">{deal.title}</h4>
                      <p className="text-sm text-muted-foreground">
                        {deal.brand_name} • {deal.deal_type.replace(/_/g, " ")}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold">{formatCurrency(deal.amount)}</span>
                      <Badge variant={getStatusColor(deal.status) as any}>
                        {deal.status.replace(/_/g, " ")}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No deals yet</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => navigate("/athlete/deals/submit")}
                >
                  Submit Your First Deal
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Compensation History */}
        {compensation && compensation.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Revenue Sharing Compensation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {compensation.slice(0, 5).map((comp) => (
                  <div
                    key={comp.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium">Revenue Sharing Distribution</h4>
                      <p className="text-sm text-muted-foreground">
                        {comp.distribution_date
                          ? new Date(comp.distribution_date).toLocaleDateString()
                          : "Pending"}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold">{formatCurrency(Number(comp.amount))}</span>
                      <Badge variant={comp.status === "approved" ? "default" : "secondary"}>
                        {comp.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      </div>
    </div>
  );
};

export default AthletePortal;
